import React, { useEffect, useState } from 'react';
import { Video as VideoIcon } from 'lucide-react';
import api from '../../../lib/api';
import { useLang, tx } from '../../../i18n/LanguageContext';
import { RichTextEditor } from '../../RichTextEditor';
import { inputCls, Field } from '../ui';
import { QuizBuilder } from './QuizBuilder';

const Seg = ({ value, onChange, options, testId }) => (
  <div className="inline-flex rounded-lg bg-slate-100 p-1 mb-3" data-testid={testId}>
    {options.map(o => (
      <button key={o.v} type="button" data-testid={`${testId}-${o.v}`} onClick={() => onChange(o.v)} className={`h-8 px-3 rounded-md text-[13px] transition-colors ${value === o.v ? 'bg-white shadow-sm text-blue-600 font-semibold' : 'text-slate-500 hover:text-slate-800'}`}>{o.label}</button>
    ))}
  </div>
);

const VideoFields = ({ form, set }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  return (
    <div className="space-y-3">
      <div>
        <Field label={L('رابط YouTube', 'YouTube URL')}><input data-testid="lesson-youtube" dir="ltr" value={form.youtube} onChange={e => set({ youtube: e.target.value })} placeholder="https://youtu.be/..." className={inputCls} /></Field>
        <p className="text-xs text-slate-400 mt-1.5">{L('مدة الفيديو تُحسب تلقائياً عند الحفظ', 'Video length is detected automatically on save')}</p>
      </div>
      <label className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 hover:border-blue-400 px-4 py-3 cursor-pointer transition-colors">
        <VideoIcon className="w-5 h-5 text-blue-600 shrink-0" />
        <span className="text-[13px] text-slate-600 truncate">{form.videoFile?.name || (form.video_url ? L('يوجد فيديو مرفوع — اختر ملفاً لاستبداله', 'Uploaded video exists — pick a file to replace') : L('أو ارفع ملف فيديو (اختياري)', 'Or upload a video file (optional)'))}</span>
        <input type="file" accept="video/*" className="hidden" data-testid="lesson-video-file" onChange={e => e.target.files?.[0] && set({ videoFile: e.target.files[0] })} />
      </label>
    </div>
  );
};

const TextFields = ({ form, set, lessonKey }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get('/posts').then(({ data }) => setPosts(data || [])).catch(() => {}); }, []);
  return (
    <div>
      <Seg testId="lesson-text-mode" value={form.textMode} onChange={v => set({ textMode: v })} options={[{ v: 'write', label: L('كتابة نص الدرس', 'Write the lesson') }, { v: 'article', label: L('ربط بمقال من المكتبة', 'Link a library article') }]} />
      {form.textMode === 'write' ? (
        <div className="grid md:grid-cols-2 gap-3">
          <Field label={L('نص الدرس (عربي)', 'Lesson text (Arabic)')}><RichTextEditor key={`c-ar-${lessonKey}`} testId="lesson-content-ar" dir="rtl" value={form.contentAr} onChange={v => set({ contentAr: v })} placeholder={L('اكتب محتوى الدرس...', 'Write the lesson...')} /></Field>
          <Field label={L('نص الدرس (إنجليزي)', 'Lesson text (English)')}><RichTextEditor key={`c-en-${lessonKey}`} testId="lesson-content-en" dir="ltr" value={form.contentEn} onChange={v => set({ contentEn: v })} placeholder="Lesson content..." /></Field>
        </div>
      ) : (
        <Field label={L('اختر المقال', 'Choose article')}>
          <select data-testid="lesson-article" value={form.article_id} onChange={e => set({ article_id: e.target.value })} className={inputCls}>
            <option value="">{L('— اختر مقالاً —', '— Pick an article —')}</option>
            {posts.map(p => <option key={p.id} value={p.id}>{p.titleAr || p.titleEn}</option>)}
          </select>
        </Field>
      )}
    </div>
  );
};

export const QuizSelect = ({ value, onChange, quizzes, optional, testId = 'lesson-quiz' }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  return (
    <select data-testid={testId} value={value} onChange={e => onChange(e.target.value)} className={inputCls}>
      <option value="">{optional ? L('بدون اختبار', 'No quiz') : L('— اختر اختباراً —', '— Pick a quiz —')}</option>
      {quizzes.map(q => <option key={q.id} value={q.id}>{q.titleAr}{q.question_count != null ? ` (${q.question_count})` : ''}</option>)}
    </select>
  );
};

const QuizFields = ({ form, set, quizzes }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  return (
    <div>
      <Seg testId="lesson-quiz-mode" value={form.quizMode} onChange={v => set({ quizMode: v })} options={[{ v: 'existing', label: L('اختيار اختبار موجود', 'Pick an existing quiz') }, { v: 'new', label: L('إنشاء أسئلة جديدة', 'Create new questions') }]} />
      {form.quizMode === 'existing'
        ? <QuizSelect value={form.quiz_id} onChange={v => set({ quiz_id: v })} quizzes={quizzes} />
        : <><p className="text-xs text-slate-500 mb-2">{L('سيُحفظ الاختبار أيضاً في مدير الاختبارات باسم الدرس.', 'The quiz is also saved in the quiz manager under the lesson title.')}</p><QuizBuilder questions={form.questions} onChange={qs => set({ questions: qs })} /></>}
    </div>
  );
};

export const TypeFields = ({ form, set, quizzes, lessonKey }) => (
  form.type === 'video' ? <VideoFields form={form} set={set} />
    : form.type === 'text' ? <TextFields form={form} set={set} lessonKey={lessonKey} />
    : <QuizFields form={form} set={set} quizzes={quizzes} />
);
